import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { getAssetUrl, resolveAssetUrl } from '../api/assets';
import { fetchPlansCached } from '../api/middleware';
import { openPreferredAppLink, openSalesOrWhatsApp } from '../utils/links';
import {
  DEFAULT_PLAN_BADGE,
  DisplayPlan,
  formatAnnualPlanText,
  formatCurrency,
  getHighlightedDisplayPlan,
  mapApiPlansToDisplay,
} from '../utils/planDisplay';
import { useTranslation, interpolate } from '../i18n';

interface HeroProps {
  variables?: SectionVariable;
  name?: string;
}

/* ── Inline SVG Icons ── */

function SmartphoneIcon({ color = 'var(--color-accent-info, #00BFFF)', size = 23 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.83057 9.5766C3.83057 5.96526 3.83057 4.15911 4.95295 3.03769C6.07532 1.91627 7.88052 1.91531 11.4919 1.91531C15.1032 1.91531 16.9093 1.91531 18.0308 3.03769C19.1522 4.16007 19.1531 5.96526 19.1531 9.5766V13.4072C19.1531 17.0186 19.1531 18.8247 18.0308 19.9462C16.9084 21.0676 15.1032 21.0685 11.4919 21.0685C7.88052 21.0685 6.07437 21.0685 4.95295 19.9462C3.83152 18.8238 3.83057 17.0186 3.83057 13.4072V9.5766Z" stroke={color} strokeWidth="1.68548"/>
      <path d="M14.3646 18.1956H8.61865" stroke={color} strokeWidth="1.68548" strokeLinecap="round"/>
    </svg>
  );
}

function HeartIcon({ color = '#fff', size = 32 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9.6075 7.55764C6.77338 8.85256 4.69775 11.9344 4.69775 15.609C4.69775 19.3622 6.23525 22.2561 8.43559 24.7366C10.2515 26.7798 12.4485 28.4745 14.5907 30.1247C15.1009 30.5176 15.6049 30.9094 16.1026 31.3001C17.0012 32.009 17.8024 32.6291 18.5763 33.0818C19.3501 33.5346 19.9703 33.7396 20.4998 33.7396C21.0294 33.7396 21.6513 33.5346 22.4234 33.0818C23.1973 32.6291 23.9985 32.009 24.8971 31.3001C25.3948 30.9083 25.8987 30.5171 26.409 30.1264C28.5512 28.4728 30.7481 26.7798 32.5641 24.7366C34.7661 22.2561 36.3019 19.3622 36.3019 15.609C36.3019 11.9361 34.2263 8.85256 31.3922 7.55764C28.6383 6.2986 24.9381 6.63173 21.4223 10.2858C21.3028 10.4099 21.1595 10.5085 21.001 10.5759C20.8425 10.6432 20.6721 10.6779 20.4998 10.6779C20.3276 10.6779 20.1572 10.6432 19.9987 10.5759C19.8401 10.5085 19.6969 10.4099 19.5773 10.2858C16.0616 6.63173 12.3613 6.2986 9.6075 7.55764Z" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ConnectivityIcon({ color = 'var(--color-primary, #6B00D6)', size = 23 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.4921 21.0685C16.7813 21.0685 21.0688 16.7811 21.0688 11.4919C21.0688 6.20276 16.7813 1.91531 11.4921 1.91531C6.20298 1.91531 1.91553 6.20276 1.91553 11.4919C1.91553 13.0242 2.27561 14.4722 2.91437 15.7564C3.08483 16.0973 3.14133 16.4871 3.04269 16.8558L2.47289 18.9875C2.41645 19.1985 2.4165 19.4206 2.47303 19.6315C2.52957 19.8425 2.64059 20.0348 2.79496 20.1893C2.94933 20.3438 3.14162 20.4549 3.35252 20.5116C3.56343 20.5682 3.78553 20.5684 3.99653 20.5121L6.12828 19.9414C6.4983 19.8479 6.88975 19.8932 7.22863 20.0687C8.55303 20.7282 10.0127 21.0704 11.4921 21.0685Z" stroke={color} strokeWidth="1.68548"/>
      <path d="M11.4923 15.3226V7.66129M7.66162 13.4073V9.57661M15.3229 13.4073V9.57661" stroke={color} strokeWidth="1.68548" strokeLinecap="round"/>
    </svg>
  );
}

function CurvedArrowSvg() {
  return (
    <svg width="48" height="30" viewBox="0 0 48 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <path d="M0.206809 17.8616C1.30211 19.8142 2.39816 21.7666 3.49345 23.7192L5.20253 26.7651C5.73074 27.7069 6.29729 28.6598 7.40876 28.9701C8.72843 29.3385 9.7481 28.5356 10.6329 27.6752C11.4188 26.9106 12.2054 26.1459 12.9913 25.3813C13.7758 24.6179 14.5898 23.8755 15.3496 23.0875C16.123 22.286 17.1149 21.2953 17.4829 20.2343C17.4627 20.2333 17.4426 20.2323 17.4227 20.2321C17.5775 19.7027 17.4021 19.0858 16.9835 18.7792C16.8009 18.3119 16.4372 17.9219 15.8903 17.7774C14.2688 17.3484 12.6322 16.9817 10.9903 16.6441C11.1548 16.2199 11.3328 15.8021 11.5262 15.3959C13.0219 12.2559 15.3338 9.49864 18.1459 7.45648C23.3883 3.65047 30.6491 2.07656 36.7366 4.7381C38.6406 5.57094 40.4186 6.82955 41.9563 8.21636C43.5492 9.65336 44.9227 11.3721 46.5893 12.7142C47.392 13.3609 48.2559 12.2317 47.9193 11.4704C47.073 9.55618 45.5128 7.72671 44.0776 6.22495C42.6202 4.69886 40.9844 3.30032 39.1321 2.27666C35.7191 0.390895 31.8829 -0.268461 28.0202 0.0963941C20.2224 0.832371 12.872 5.60059 9.06043 12.4533C8.43049 13.5862 7.90089 14.7718 7.47084 15.9926C5.60767 15.6857 3.73728 15.4203 1.8588 15.2307C0.497289 15.093 -0.434206 16.7189 0.206809 17.8616Z" fill="white"/>
    </svg>
  );
}

function ArrowUpRightCircledIcon({ color = 'var(--color-accent-info, #00BFFF)', size = 36 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9.5" stroke={color} strokeWidth="2.5" />
      <path d="M9.5 14.5L14.5 9.5M14.5 9.5H10.5M14.5 9.5V13.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function WhatsAppIconCircled({ size = 44 }: { size?: number }) {
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: '50%', bgcolor: 'var(--color-whatsapp, #25D366)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51h-.57c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M21.18 2.812A11.812 11.812 0 0012.053 0C5.462 0 .101 5.36.098 11.954c0 2.105.55 4.16 1.595 5.975L0 24l6.216-1.63a11.83 11.83 0 005.834 1.523h.005c6.589 0 11.951-5.361 11.955-11.955a11.832 11.832 0 00-2.83-8.126"/>
      </svg>
    </Box>
  );
}

function WazeIconCircled({ size = 44 }: { size?: number }) {
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: '12px', bgcolor: 'var(--color-waze, #33CCFF)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size * 0.75} height={size * 0.75} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="35" cy="82" r="11" fill="black"/>
        <circle cx="65" cy="82" r="11" fill="black"/>
        <path d="M50 18 C 22 18 12 38 12 55 C 12 60 14 64 16 67 C 16 67 8 77 8 77 C 8 77 18 76 24 79 C 31 83 40 84 50 84 C 78 84 88 64 88 55 C 88 38 78 18 50 18 Z" fill="white"/>
        <circle cx="40" cy="46" r="5" fill="black"/>
        <circle cx="60" cy="46" r="5" fill="black"/>
        <path d="M36 58 Q 50 70 64 58" fill="none" stroke="black" strokeWidth="4.5" strokeLinecap="round"/>
      </svg>
    </Box>
  );
}

function getHeroApps(apps: string[] | undefined, fallbackList: string): string[] {
  const validApps = apps?.map((app) => app.trim()).filter(Boolean) || [];
  if (validApps.length) return validApps;
  return fallbackList.split(',').map((s) => s.trim()).filter(Boolean);
}

export function Hero({ variables }: HeroProps) {
  const { app, settings } = useSettings();
  const { t } = useTranslation();
  const [featuredPlan, setFeaturedPlan] = useState<DisplayPlan | null>(null);
  const texts = variables?.text || [];
  const images = variables?.image || [];
  const appLinks = settings.appLinks;

  // Text variables (translation-aware defaults; settings still override)
  const headlineStr = texts[0] || t.hero.defaultHeadline;
  const headlineParts = headlineStr.split(' ');
  const highlightedWord = headlineParts.length > 1 ? headlineParts.pop() : '';
  const mainHeadline = headlineParts.join(' ');
  const headlineBold = texts[1] || t.hero.defaultHeadlineBold;
  const subtitle = texts[2] || t.hero.defaultSubtitle;
  const planLabel = texts[3] || t.hero.defaultPlanLabel;
  const planData = texts[4] || t.hero.defaultPlanData;
  const planUnit = texts[5] || t.hero.defaultPlanUnit;
  const planFeatures = texts[6] || t.hero.defaultPlanFeatures;
  const planBenefits = texts[7] || t.hero.defaultPlanBenefits;
  const planPrice = texts[8] || t.hero.defaultPlanPrice;
  const planInstallment = texts[9] || t.hero.defaultPlanInstallment;

  // New text variables
  const appButtonText = texts[10] || t.hero.defaultAppCta;
  const hideAppButton = settings.hideApp === true || texts[11] === 'true';
  const curvedText = texts[12] || t.hero.defaultCurvedText;
  const disclaimerText = texts[13] || t.hero.defaultDisclaimer;
  const promoText = texts[14] || t.hero.defaultPromoText;
  const priceLabel = texts[15] || t.hero.defaultPriceLabel;

  useEffect(() => {
    if (!settings.middlewareUrl) return;

    let cancelled = false;

    fetchPlansCached(settings.middlewareUrl)
      .then((apiPlans) => {
        if (cancelled || !apiPlans.length) return;

        // MVNEs Surf não somam o bônus +5GB automaticamente na exibição
        const highlightedPlan = getHighlightedDisplayPlan(
          mapApiPlansToDisplay(apiPlans, {
            skipPortabilityBonus: settings.isSurfMVNE === true,
          })
        );
        if (highlightedPlan) {
          setFeaturedPlan(highlightedPlan);
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar plano em destaque da API:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [settings.middlewareUrl, settings.isSurfMVNE]);

  const rawBadge = featuredPlan?.badge;
  const displayedPlanLabel = rawBadge
    ? (rawBadge === DEFAULT_PLAN_BADGE ? t.plans.mostPopular : rawBadge)
    : planLabel;
  const displayedPlanData = featuredPlan?.dataAmount || planData;
  const displayedPlanUnit = featuredPlan?.dataUnit.toLowerCase() || planUnit;
  const displayedPlanBenefits = planBenefits;
  const displayedPlanPrice = featuredPlan
    ? formatCurrency(featuredPlan.monthlyPriceValue, false)
    : planPrice;
  const displayedPlanInstallment = featuredPlan
    ? formatAnnualPlanText(featuredPlan.monthlyPriceValue)
    : planInstallment;
  let displayedApps = getHeroApps(featuredPlan?.appsIncluded, t.hero.fallbackApps);
  if (settings.isSurfMVNE) {
    displayedApps = displayedApps.filter(
      (appName) => !appName.toLowerCase().includes('whatsapp') && !appName.toLowerCase().includes('waze')
    );
  }

  const heroImage = images[0]
    ? resolveAssetUrl(images[0], app)
    : getAssetUrl('images/hero/hero-woman.png', app);

  function handleAppDownload() {
    openPreferredAppLink(appLinks);
  }

  /* ── Shared sub-components ── */

  const heroImageSection = (mobile: boolean) => (
    <Box
      sx={{
        flex: mobile ? undefined : '1 1 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        minHeight: mobile ? 320 : 640,
        py: mobile ? 2 : 0,
        mb: mobile ? -8 : 0,
        zIndex: 2,
      }}
    >
      {/* Outermost ring */}
      <Box
        sx={{
          position: 'absolute',
          width: mobile ? 300 : 680,
          height: mobile ? 360 : 850,
          borderRadius: mobile ? '120px' : '300px',
          bgcolor: 'var(--color-purple-glow, #7300FF)',
          opacity: 0.25,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
        }}
      />
      {/* Middle ring */}
      <Box
        sx={{
          position: 'absolute',
          width: mobile ? 250 : 560,
          height: mobile ? 300 : 680,
          borderRadius: mobile ? '100px' : '240px',
          bgcolor: 'var(--color-purple-glow, #7300FF)',
          opacity: 0.5,
          boxShadow: '0 0 54.708px #00000040',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
        }}
      />
      {/* Inner ring */}
      <Box
        sx={{
          position: 'absolute',
          width: mobile ? 200 : 440,
          height: mobile ? 240 : 540,
          borderRadius: mobile ? '80px' : '180px',
          bgcolor: 'var(--color-purple-glow, #7300FF)',
          opacity: 0.75,
          boxShadow: '0 0 54.708px #00000040',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
        }}
      />
      {/* Woman image */}
      <Box
        sx={{
          position: 'relative',
          top: '-40px',
          width: mobile ? 180 : 340,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        <Box
          component="img"
          src={heroImage}
          alt="Hero"
          sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
        />
      </Box>

      {/* Floating smartphone badge */}
      <Box
        sx={{
          position: 'absolute',
          top: mobile ? '10%' : '10%',
          left: mobile ? '5%' : '12%',
          width: mobile ? 44 : 60,
          height: mobile ? 44 : 60,
          borderRadius: '16px',
          bgcolor: 'var(--color-accent-info, #00BFFF)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 24px #00BFFF4D',
          zIndex: 3,
        }}
      >
        <SmartphoneIcon color="#fff" size={mobile ? 22 : 28} />
      </Box>

      {/* Floating heart badge */}
      <Box
        sx={{
          position: 'absolute',
          bottom: mobile ? '10%' : '10%',
          right: mobile ? '5%' : '8%',
          width: mobile ? 44 : 60,
          height: mobile ? 44 : 60,
          borderRadius: '16px',
          bgcolor: 'var(--color-accent-info, #00BFFF)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 24px #00BFFF4D',
          zIndex: 3,
        }}
      >
        <HeartIcon color="#fff" size={mobile ? 22 : 28} />
      </Box>
    </Box>
  );

  const planCard = (mobile: boolean) => (
    <Box sx={{ color: '#fff', position: 'relative', zIndex: 3 }}>
      {/* PLANO PRO & Promo - desktop only (mobile renders this separately above the image) */}
      {!mobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
              border: '1px solid #fff',
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {displayedPlanLabel}
          </Box>

          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: -2, mr: -4 }}>
            <Typography
              sx={{
                color: '#fff',
                fontFamily: "'Figma Hand', 'Caveat', cursive",
                fontSize: '1.2rem',
                transform: 'rotate(-4deg)',
                width: '180px',
                lineHeight: 1.2,
              }}
            >
              {promoText}
            </Typography>
            <svg width="40" height="30" viewBox="0 0 40 30" fill="none" style={{ alignSelf: 'flex-start', marginLeft: '10px', marginTop: '4px' }}>
              <path d="M35 5 Q 30 25 15 25 T 5 20 M 5 20 L 12 15 M 5 20 L 10 26" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </Box>
        </Box>
      )}

      {mobile ? (
        /* ── Mobile: 2-column layout ── */
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          {/* Left column: GB data + features */}
          <Box>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                sx={{
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '3.5rem',
                  fontWeight: 800,
                  lineHeight: 1,
                  letterSpacing: '-0.05rem',
                  color: '#fff',
                }}
              >
                {displayedPlanData}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#FFFFFF66',
                  ml: 0.5,
                }}
              >
                {displayedPlanUnit}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '1.1rem', color: '#fff', fontWeight: 600, mb: 0.5 }}>
              {planFeatures}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '0.9rem' }}>
              {displayedPlanBenefits}
            </Typography>
          </Box>

          {/* Right column: Price + Apps */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            {/* Price */}
            <Box>
              <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '0.85rem', mb: 0.5 }}>
                {priceLabel}
              </Typography>
              <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '2.2rem', fontWeight: 800, lineHeight: 1, mb: 0.5 }}>
                {displayedPlanPrice}
              </Typography>
              <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '0.8rem' }}>
                {displayedPlanInstallment}
              </Typography>
            </Box>

            {/* Apps */}
            <Box display="flex" gap={3} mt={2}>
              {displayedApps.map((appName) => {
                const normalizedAppName = appName.toLowerCase();
                const isWhatsApp = normalizedAppName.includes('whatsapp');
                const isWaze = normalizedAppName.includes('waze');

                return (
                  <Box key={appName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isWaze ? <WazeIconCircled size={36} /> : <WhatsAppIconCircled size={36} />}
                    <Box>
                      <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '0.8rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                        {isWhatsApp ? 'Whatsapp' : isWaze ? 'Waze' : appName}
                      </Typography>
                      <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '0.7rem', color: '#FFFFFFCC', lineHeight: 1 }}>
                        {t.common.unlimited}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      ) : (
        /* ── Desktop: single column layout ── */
        <>
          {/* GB Data */}
          <Box display="flex" alignItems="baseline" mb={1}>
            <Typography
              sx={{
                fontFamily: "'Figtree', sans-serif",
                fontSize: '4.5rem',
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: '-0.05rem',
                color: '#fff',
              }}
            >
              {displayedPlanData}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Figtree', sans-serif",
                fontSize: '2.5rem',
                fontWeight: 700,
                color: '#FFFFFF66',
                ml: 0.5,
              }}
            >
              {displayedPlanUnit}
            </Typography>
          </Box>

          {/* Features */}
          <Typography variant="body2" sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '1.25rem', color: '#fff', fontWeight: 600, mb: 0.5 }}>
            {planFeatures}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '1rem', mb: 4 }}>
            {displayedPlanBenefits}
          </Typography>

          {/* Apps */}
          <Box display="flex" gap={4} mb={6}>
            {displayedApps.map((appName) => {
              const normalizedAppName = appName.toLowerCase();
              const isWhatsApp = normalizedAppName.includes('whatsapp');
              const isWaze = normalizedAppName.includes('waze');

              return (
                <Box key={appName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isWaze ? <WazeIconCircled /> : <WhatsAppIconCircled />}
                  <Box>
                    <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                      {isWhatsApp ? 'Whatsapp' : isWaze ? 'Waze' : appName}
                    </Typography>
                    <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '0.8rem', color: '#FFFFFFCC', lineHeight: 1 }}>
                      {t.common.unlimited}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Price */}
          <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '0.85rem', mb: 0.5 }}>
            {priceLabel}
          </Typography>
          <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '2.5rem', fontWeight: 800, lineHeight: 1, mb: 0.5 }}>
            {displayedPlanPrice}
          </Typography>
          <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontSize: '0.85rem', mb: 4 }}>
            {displayedPlanInstallment}
          </Typography>

          {/* Buy button */}
          <Box
            component="button"
            onClick={() => openSalesOrWhatsApp(
              settings.salesUrl,
              settings.socials?.whatsapp,
              interpolate(t.whatsapp.salesMessage, { brand: settings.companyName || t.whatsapp.salesFallbackBrand })
            )}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: '#00BFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
            <Typography sx={{ fontFamily: "'Figtree', sans-serif", color: '#fff', fontWeight: 600, fontSize: '1rem' }}>
              {t.common.buyNow}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box
      className="hero"
      sx={{
        bgcolor: 'var(--color-surface-base, #FFF)',
        py: { xs: 1.5, md: 2 },
        px: { xs: 1.5, md: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          borderRadius: { xs: '16px', md: '24px' },
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'var(--color-primary-dark, #4A0099)',
          minHeight: { xs: 'auto', md: 520 },
        }}
      >
        {/* ===== MOBILE LAYOUT ===== */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, py: 3, position: 'relative', zIndex: 1 }}>
          {/* Row 1: Small icons */}
          <Box display="flex" alignItems="center" gap={2} mb={2.5}>
            <SmartphoneIcon color="#00BFFF" size={28} />
            <HeartIcon color="#fff" size={32} />
            <ConnectivityIcon color="#00BFFF" size={28} />
          </Box>

          {/* Row 2: Headline (left) | Subtitle (right) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3, alignItems: 'center' }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Figtree', sans-serif",
                  fontWeight: 600,
                  fontSize: '1.6rem',
                  lineHeight: 1.1,
                  letterSpacing: '-0.05rem',
                  color: '#FFF',
                }}
              >
                {mainHeadline}{' '}
                <Box component="span" sx={{ color: '#00BFFF' }}>
                  {highlightedWord}
                </Box>
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Figtree', sans-serif",
                    fontWeight: 600,
                    fontSize: '1.6rem',
                    lineHeight: 1.1,
                    letterSpacing: '-0.05rem',
                    color: '#FFF',
                  }}
                >
                  {headlineBold}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                color: '#FFF',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '0.85rem',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* PLANO PRO & Promo - mobile only */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 2,
                py: 0.5,
                borderRadius: '20px',
                border: '1px solid #fff',
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {displayedPlanLabel}
            </Box>
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                sx={{
                  color: '#fff',
                  fontFamily: "'Figma Hand', 'Caveat', cursive",
                  fontSize: '1rem',
                  transform: 'rotate(-4deg)',
                  width: '140px',
                  lineHeight: 1.2,
                }}
              >
                {promoText}
              </Typography>
              <svg width="40" height="30" viewBox="0 0 40 30" fill="none" style={{ alignSelf: 'flex-start', marginLeft: '10px', marginTop: '4px' }}>
                <path d="M35 5 Q 30 25 15 25 T 5 20 M 5 20 L 12 15 M 5 20 L 10 26" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </Box>
          </Box>

          {/* Plan card */}
          {planCard(true)}

          {/* Hero image with rings + disclaimer overlay - LAST on mobile */}
          <Box sx={{ position: 'relative' }}>
            {heroImageSection(true)}
            <Typography
              sx={{
                position: 'absolute',
                bottom: 40,
                left: '50%',
                transform: 'translateX(-50%)',
                color: '#FFFFFF80',
                fontSize: '0.6rem',
                lineHeight: 1.4,
                textAlign: 'center',
                width: '90%',
                zIndex: 4,
              }}
            >
              {disclaimerText}
            </Typography>
          </Box>
        </Box>

        {/* ===== DESKTOP LAYOUT ===== */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            px: 5,
            py: 4,
            flexDirection: 'row',
            alignItems: 'stretch',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Left content */}
          <Box
            sx={{
              flex: '0 0 auto',
              width: '32%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              pr: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={2.5}>
              <SmartphoneIcon color="#00BFFF" size={28} />
              <HeartIcon color="#fff" size={32} />
              <ConnectivityIcon color="#00BFFF" size={28} />
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontFamily: "'Figtree', sans-serif",
                fontWeight: 600,
                fontSize: '3.90213rem',
                lineHeight: 'normal',
                letterSpacing: '-0.15606rem',
                mb: 0,
                color: '#FFF',
              }}
            >
              {mainHeadline}{' '}
              <Box component="span" sx={{ color: '#00BFFF' }}>
                {highlightedWord}
              </Box>
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: "'Figtree', sans-serif",
                fontWeight: 600,
                fontSize: '3.90213rem',
                lineHeight: 'normal',
                letterSpacing: '-0.15606rem',
                mb: 2,
                color: '#FFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ArrowUpRightCircledIcon color="#00BFFF" size={60} />
              </Box>
              <Box component="span" sx={{ color: '#FFF' }}>
                {headlineBold}
              </Box>
            </Typography>

            <Typography
              sx={{
                color: '#FFF',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '1.25rem',
                fontWeight: 500,
                lineHeight: 'normal',
                letterSpacing: '-0.05rem',
                mb: 3,
              }}
            >
              {subtitle}
            </Typography>

            <Box display="flex" alignItems="center" gap={3} mb={3}>
              {!hideAppButton && (
                <Box
                  component="button"
                  onClick={handleAppDownload}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: '#00000059',
                    border: 'none',
                    borderRadius: '40px',
                    pr: 2.5,
                    pl: 0.8,
                    py: 0.8,
                    position: 'relative',
                    top: '30px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: '#00000073' },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#00000066',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <SmartphoneIcon color="#00BFFF" size={20} />
                  </Box>
                  <Typography
                    sx={{
                      color: '#FFF',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textAlign: 'left',
                      lineHeight: 1.2,
                      maxWidth: '100px',
                    }}
                  >
                    {appButtonText}
                  </Typography>
                </Box>
              )}

              {!hideAppButton && (
                <Box sx={{ position: 'relative', ml: 3, mt: 1 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-22px',
                      left: '-32px',
                      transform: 'scale(1.15) rotate(2deg)',
                      pointerEvents: 'none',
                    }}
                  >
                    <CurvedArrowSvg />
                  </Box>
                  <Typography
                    sx={{
                      color: '#FFF',
                      fontFamily: "'Figma Hand', 'Caveat', cursive",
                      fontSize: '0.61119rem',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '151.553%',
                      width: '7.61894rem',
                      transform: 'rotate(-4.352deg)',
                    }}
                  >
                    {curvedText}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography
              sx={{
                color: '#FFFFFF80',
                fontSize: '0.6rem',
                lineHeight: 1.4,
                maxWidth: 360,
                mt: 'auto',
              }}
            >
              {disclaimerText}
            </Typography>
          </Box>

          {/* Center - Hero Image */}
          {heroImageSection(false)}

          {/* Right - Plan Card */}
          <Box sx={{ flex: '0 0 auto', width: 280, alignSelf: 'center' }}>
            {planCard(false)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
